/**
 * MessageBubble - Individual chat message
 * 
 * Handles text, image, and file message types with avatar.
 */
import { UserAvatar } from "./UserAvatar";
import type { ChatMessage } from "../types";

interface MessageBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
  showAvatar?: boolean;
  onImageClick?: (url: string) => void;
  resolveUrl?: (url: string | null) => string;
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MessageBubble({ 
  message, 
  isOwn, 
  showAvatar = true,
  onImageClick,
  resolveUrl = (url) => url || ""
}: MessageBubbleProps) {
  const senderName = message.sender?.full_name || "Unknown";
  const time = new Date(message.created_at).toLocaleTimeString([], { 
    hour: "2-digit", 
    minute: "2-digit" 
  });

  const bubbleClasses = isOwn
    ? "bg-primary text-primary-foreground ml-auto"
    : "bg-muted text-foreground";

  const containerClasses = isOwn
    ? "flex-row-reverse"
    : "flex-row";

  // Image message
  if (message.message_type === "image" && message.attachment_url) {
    const imageUrl = resolveUrl(message.attachment_url);
    return (
      <div className={`flex gap-2 ${containerClasses} mb-2`}>
        {showAvatar && (
          <UserAvatar name={senderName} size="sm" />
        )}
        <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
          {!isOwn && (
            <span className="text-xs text-muted-foreground mb-1 px-1">{senderName}</span>
          )}
          <div className={`rounded-lg overflow-hidden ${bubbleClasses} p-1`}>
            <img
              src={imageUrl}
              alt="attachment"
              className="max-w-[240px] rounded cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onImageClick?.(imageUrl)}
            />
          </div>
          <span className="text-[10px] text-muted-foreground mt-1 px-1">{time}</span>
        </div>
      </div>
    );
  }

  // File message
  if (message.message_type === "file" && message.attachment_url) {
    const fileUrl = resolveUrl(message.attachment_url);
    return (
      <div className={`flex gap-2 ${containerClasses} mb-2`}>
        {showAvatar && (
          <UserAvatar name={senderName} size="sm" />
        )}
        <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
          {!isOwn && (
            <span className="text-xs text-muted-foreground mb-1 px-1">{senderName}</span>
          )}
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-3 rounded-lg ${bubbleClasses} hover:opacity-90 transition-opacity`}
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="flex flex-col overflow-hidden">
              <span className="text-sm font-medium truncate max-w-[150px]">
                {message.attachment_name || "File"}
              </span>
              <span className="text-xs opacity-70">
                {formatFileSize(message.attachment_size)}
              </span>
            </div>
            <svg className="w-4 h-4 shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
          <span className="text-[10px] text-muted-foreground mt-1 px-1">{time}</span>
        </div>
      </div>
    );
  }

  // Text message (default)
  return (
    <div className={`flex gap-2 ${containerClasses} mb-2`}>
      {showAvatar && (
        <UserAvatar name={senderName} size="sm" />
      )}
      <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
        {!isOwn && (
          <span className="text-xs text-muted-foreground mb-1 px-1">{senderName}</span>
        )}
        <div className={`px-3 py-2 rounded-lg ${bubbleClasses}`}>
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <span className="text-[10px] text-muted-foreground mt-1 px-1">{time}</span>
      </div>
    </div>
  );
}
